import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { usePageTitle } from '@/shared/usePageTitle';
import { ExpiryAlerts } from '@/shared/ExpiryAlerts';
import { useToast } from '@/shared/toast';
import { useConfirm } from '@/shared/confirm';
import { REFETCH_LIST_MS } from '@/shared/timing';
import { useAuth } from '@/shared/AuthContext';
import { CreateSelectiveModal, ActiveSelectivesModal } from '@/features/selectives/SelectivesModals';
import { InterventionEditModal } from './InterventionEditModal';
import { AchievementsRibbon } from './AchievementsRibbon';

interface Intervention { id: string; date: string; startTime: string; endTime: string; note: string; category?: string }
interface InterventionsPage { total: number; page: number; pageSize: number; items: Intervention[] }

const OPERATIONAL_ROLES = ['Utente', 'Magazzino', 'TecnicoIdranti', 'RisorseUmane', 'Amministratore'];

export function DashboardPage(): React.JSX.Element {
  usePageTitle('Registro Interventi');
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  // Audit techlead #5: useAuth invece di useState locale + localStorage diretto.
  // Cosi' un cambio di roles lato server propaga subito alla UI senza reload.
  const { user } = useAuth();
  const [showCreateSelective, setShowCreateSelective] = useState(false);
  const [showActiveSelectives, setShowActiveSelectives] = useState(false);
  const [editingIntervention, setEditingIntervention] = useState<Intervention | null>(null);
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear());

  const deleteIntervention = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/interventions/${id}`); },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interventions.list'] });
      qc.invalidateQueries({ queryKey: ['interventions.stats.yearly'] });
      qc.invalidateQueries({ queryKey: ['interventions.stats.monthly'] });
      toast.show('Intervento eliminato', 'success');
    },
    onError: () => toast.show('Errore eliminazione', 'error'),
  });

  async function handleDelete(it: Intervention): Promise<void> {
    const ok = await confirm({
      title: 'Elimina intervento',
      message: `Eliminare l'intervento del ${new Date(it.date).toLocaleDateString('it-IT')}? Operazione irreversibile.`,
      confirmLabel: '🗑️ Elimina',
      cancelLabel: 'Annulla',
      variant: 'danger',
      icon: '🗑️',
    });
    if (ok) deleteIntervention.mutate(it.id);
  }

  const isOperative = useMemo(
    () => (user?.roles ?? []).some(r => OPERATIONAL_ROLES.includes(r)),
    [user],
  );

  const { data: summary } = useQuery({
    queryKey: ['dashboard.summary'],
    queryFn: async () => (await api.get('/dashboard/summary')).data,
  });

  // Conteggio selettive attive per badge pulse sul bottone (Bryan).
  // Polling 15s + invalidato a ogni selectiveCreated/Terminated via SignalR.
  const { data: activeSelectives } = useQuery<{ id: string }[]>({
    queryKey: ['selectives.active'],
    queryFn: async () => (await api.get('/selectives/active')).data,
    refetchInterval: REFETCH_LIST_MS,
  });
  const activeSelectivesCount = activeSelectives?.length ?? 0;

  const { data: interventionsPage } = useQuery<InterventionsPage>({
    queryKey: ['interventions.list', user?.id, filterYear],
    queryFn: async () => (await api.get('/interventions', { params: { year: filterYear } })).data,
    enabled: !!user && isOperative,
  });
  const interventions = interventionsPage?.items ?? [];

  const currentYear = new Date().getFullYear();
  const yearsOptions = Array.from({ length: 10 }, (_, i) => currentYear - i);

  if (!isOperative) {
    return <AdminDashboard summary={summary} />;
  }

  return (
    <div className="page-container">
      <ExpiryAlerts />

      {/* Due bottoni rossi grandi (replica esatta legacy) — aprono popup */}
      <div className="quick-actions">
        <button className="quick-action danger" onClick={() => setShowCreateSelective(true)}>
          <span className="quick-action-icon">🚨</span>
          <span className="quick-action-label">+SELETTIVA</span>
        </button>
        <button className="quick-action danger-light" onClick={() => setShowActiveSelectives(true)}
          style={{ position: 'relative' }}>
          <span className="quick-action-icon">📋</span>
          <span className="quick-action-label">Selettive Attive</span>
          {activeSelectivesCount > 0 && (
            <span className="active-selectives-badge">{activeSelectivesCount}</span>
          )}
        </button>
      </div>

      {/* Achievement medaglie — solo il nome (Bryan: niente data/categoria).
          Seguono l'anno selezionato nel filtro sotto (achievement per anno). */}
      <AchievementsRibbon year={filterYear} />

      {/* Filtro anno tabella */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, margin: '16px 0 8px' }}>
        <label style={{ fontSize: 14, color: '#666', fontWeight: 600 }}>Anno:</label>
        <select value={filterYear} onChange={e => setFilterYear(parseInt(e.target.value, 10))}
          style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #ced4da', fontSize: 14 }}>
          {yearsOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {showCreateSelective && <CreateSelectiveModal onClose={() => setShowCreateSelective(false)} />}
      {showActiveSelectives && <ActiveSelectivesModal onClose={() => setShowActiveSelectives(false)} />}

      {/* Tabella interventi propri — colonne data/ora compatte, nota flex,
          azioni in colonna verticale per ridurre lo spazio orizzontale */}
      <div className="table-wrapper interventions-table-compact">
        <table>
          <thead>
            <tr>
              <th className="col-date">Data</th>
              <th className="col-time">Ora ↑</th>
              <th className="col-time">Ora ↓</th>
              <th>Nota</th>
              <th className="col-actions">Az.</th>
            </tr>
          </thead>
          <tbody>
            {interventions.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30, color: '#888' }}>
                Nessun intervento registrato. Usa la sidebar per inserirne uno.
              </td></tr>
            )}
            {interventions.map(i => (
              <tr key={i.id}>
                <td className="col-date">{new Date(i.date).toLocaleDateString('it-IT')}</td>
                <td className="col-time">{i.startTime}</td>
                <td className="col-time">{i.endTime}</td>
                <td>{i.note}</td>
                <td className="col-actions">
                  <div className="row-actions-col">
                    <button onClick={() => setEditingIntervention(i)} title="Modifica"
                      className="action-btn action-edit">✎</button>
                    <button onClick={() => handleDelete(i)} title="Elimina"
                      className="action-btn action-delete">🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingIntervention && (
        <InterventionEditModal it={editingIntervention}
          onClose={() => setEditingIntervention(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['interventions.list'] });
            qc.invalidateQueries({ queryKey: ['interventions.stats.yearly'] });
            qc.invalidateQueries({ queryKey: ['interventions.stats.monthly'] });
            setEditingIntervention(null);
            toast.show('Intervento aggiornato', 'success');
          }} />
      )}
    </div>
  );
}

function AdminDashboard({ summary }: { summary: { totalUsers?: number; totalTenants?: number; totalInterventions?: number; activeSelectives?: number } | undefined }): React.JSX.Element {
  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>⚙️ Amministrazione Sistema</h1>
          <div className="page-header-subtitle">Pannello SuperAdmin — accesso globale a tutti i distaccamenti</div>
        </div>
      </div>

      <div className="stats-grid">
        <StatCard variant="success" title="Distaccamenti attivi" value={summary?.totalTenants ?? '—'} />
        <StatCard variant="info"    title="Utenti totali"        value={summary?.totalUsers ?? '—'} />
        <StatCard variant="danger"  title="Selettive attive"     value={summary?.activeSelectives ?? '—'} />
        <StatCard variant="accent"  title="Interventi totali"    value={summary?.totalInterventions ?? '—'} />
      </div>
    </div>
  );
}

function StatCard({ title, value, variant }: {
  title: string; value: number | string; variant: 'success' | 'info' | 'danger' | 'accent';
}): React.JSX.Element {
  return (
    <div className={`stat-card stat-${variant}`}>
      <div className="stat-card-title">{title}</div>
      <div className="stat-card-value">{value}</div>
    </div>
  );
}
